package info.rezident.app.data

import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.functions.functions
import info.rezident.app.core.Logger
import info.rezident.app.data.quiz.RecommendationItem
import info.rezident.app.data.quiz.RecommendationsResult
import info.rezident.app.data.quiz.SpecialtyMetadataRepository
import info.rezident.app.data.quiz.UserAnswer
import kotlinx.coroutines.delay
import kotlinx.serialization.Serializable

/**
 * Response model for computeMatchScores Cloud Function
 */
@Serializable
data class ComputeMatchScoresResponse(
    val topMatches: List<MatchResult>,
    val worstMatches: List<MatchResult>
)

/**
 * Model for individual match result
 */
@Serializable
data class MatchResult(
    val specialty: String,
    val score: Int,
    val analysis: MatchAnalysis
)

/**
 * Model for match analysis containing 4 categories of question matches.
 * Each category represents a different type of alignment between user and specialty:
 * - perfectAnswersHigh: Both user and specialty scored high (4-5) on these questions
 * - perfectAnswersLow: Both user and specialty scored low (1-2) on these questions
 * - differentAnswersHigh: User scored high but specialty scored low on these questions
 * - differentAnswersLow: User scored low but specialty scored high on these questions
 */
@Serializable
data class MatchAnalysis(
    val perfectAnswersHigh: List<String> = emptyList(),
    val perfectAnswersLow: List<String> = emptyList(),
    val differentAnswersHigh: List<String> = emptyList(),
    val differentAnswersLow: List<String> = emptyList()
)

/**
 * Repository for calling Firebase Cloud Functions
 */
object CloudFunctionsRepository {
    private val functions = Firebase.functions("europe-west4")

    /**
     * Calls the computeMatchScores Cloud Function to generate specialty recommendations
     * based on user's quiz answers.
     *
     * Implements retry logic: 2 retries with 5-second delays between attempts.
     *
     * @param answers List of user answers from the quiz
     * @return RecommendationsResult with top 5 specialty matches, or null on error
     */
    suspend fun computeMatchScores(answers: List<UserAnswer>): RecommendationsResult? {
        val maxRetries = 2
        val retryDelayMs = 5000L // 5 seconds

        // Build answers map with question IDs. Q1-Q26 are five-scale Int values,
        // while the following predefined-answer questions are sent as 1-indexed answer ID lists.
        val answersMap = mutableMapOf<String, Any>()

        Logger.log("Calling cloud function with answers: ")
        for (answer in answers) {
            val fiveScaleAnswer = answer.fiveScaleAnswer
            if (fiveScaleAnswer != null) {
                answersMap[answer.questionId.toString()] = fiveScaleAnswer
                Logger.log("Q${answer.questionId}: fiveScale=$fiveScaleAnswer")
                continue
            }

            // Single or multi answer as list of answer IDs
            val answerIds = answer.answerIds
            if (!answerIds.isNullOrEmpty()) {
                answersMap[answer.questionId.toString()] = answerIds
                Logger.log("Q${answer.questionId}: answerIds=${answerIds.joinToString(",")}")
            }
        }

        // Create request data - Firebase expects plain data types (Map)
        val requestData = mapOf(
            "answers" to answersMap.toMap()
        )

        // Retry loop: initial attempt + 2 retries = 3 total attempts
        repeat(maxRetries + 1) { attemptNumber ->
            try {
                if (attemptNumber == 0) {
                    Logger.log("Calling computeMatchScores Cloud Function with ${answersMap.size} answers")
                } else {
                    Logger.log("Retry attempt $attemptNumber of $maxRetries for computeMatchScores Cloud Function")
                }

                // Call the Cloud Function
                val callable = functions.httpsCallable("computeMatchScores")

                // Invoke is a suspend function that returns HttpsCallableResult
                // Generic parameter specifies the response type for deserialization
                val result = callable.invoke(requestData)

                // Parse the response - call data() to get the function response
                val response = result.data<ComputeMatchScoresResponse>()

                // Convert topMatches to RecommendationItems with all 4 analysis categories
                val items = response.topMatches.mapIndexed { index, match ->
                    RecommendationItem(
                        rank = index + 1,
                        name = match.specialty,
                        matchScore = match.score,
                        // Store all 4 categories from cloud function analysis
                        perfectAnswersHigh = match.analysis.perfectAnswersHigh,
                        perfectAnswersLow = match.analysis.perfectAnswersLow,
                        differentAnswersHigh = match.analysis.differentAnswersHigh,
                        differentAnswersLow = match.analysis.differentAnswersLow,
                        // pros/cons will be populated by AI generation later
                        pros = emptyList(),
                        cons = emptyList(),
                        characteristics = SpecialtyMetadataRepository.getCharacteristics(match.specialty)
                    )
                }

                // Convert worstMatches to RecommendationItems with all 4 analysis categories
                val incompatibleRecommendations = response.worstMatches.mapIndexed { index, match ->
                    RecommendationItem(
                        rank = index + 1,
                        name = match.specialty,
                        matchScore = match.score,
                        // Store all 4 categories from cloud function analysis
                        perfectAnswersHigh = match.analysis.perfectAnswersHigh,
                        perfectAnswersLow = match.analysis.perfectAnswersLow,
                        differentAnswersHigh = match.analysis.differentAnswersHigh,
                        differentAnswersLow = match.analysis.differentAnswersLow,
                        // pros/cons will be populated by AI generation later
                        pros = emptyList(),
                        cons = emptyList(),
                        characteristics = SpecialtyMetadataRepository.getCharacteristics(match.specialty)
                    )
                }

                Logger.log("Cloud Function returned ${items.size} top recommendations and ${incompatibleRecommendations.size} incompatible matches (attempt ${attemptNumber + 1})")
                return RecommendationsResult(
                    recommendations = items,
                    incompatibleRecommendations = incompatibleRecommendations
                )

            } catch (e: Exception) {
                val isLastAttempt = attemptNumber == maxRetries

                if (isLastAttempt) {
                    Logger.error("Error calling computeMatchScores Cloud Function after ${maxRetries + 1} attempts: ${e.message}")
                    return null
                } else {
                    Logger.warn("Error calling computeMatchScores Cloud Function (attempt ${attemptNumber + 1}): ${e.message}. Retrying in ${retryDelayMs / 1000} seconds...")
                    delay(retryDelayMs)
                }
            }
        }

        // Should never reach here due to return in loop, but added for completeness
        return null
    }
}
